import { isCardExpiryEndWithinNextMonthWindow } from "@tillix/utils";
import { computed } from "mobx";
import {
  ExtendedModel,
  isoStringToDateTransform,
  modelAction,
  tProp,
  types,
} from "mobx-keystone";
import { TModel } from "../../lib/models/t-model";
import {
  AutoPayFrequency,
  CustomAutoPayFrequency,
  MaxPaymentAmountPreference,
  PaymentMethodDisabledReason,
  PaymentMethodType,
} from "../enums";
import { autoPayEnrollmentNS, paymentMethodNS, paymentNS } from "./ns.payment";
import type { Self } from "../../lib/models/ref";
import type { ServiceAccountModel } from "../service-account/model.service-account";

@paymentMethodNS.model
export class PaymentMethodModel extends ExtendedModel(TModel, {
  nickname: tProp(types.string),
  type: tProp(types.enum(PaymentMethodType)),
  disabledReason: tProp(
    types.maybeNull(types.enum(PaymentMethodDisabledReason)),
  ),
  imageId: tProp(types.maybeNull(types.string)),
  secondaryInfo: tProp(types.maybeNull(types.string)),
  lastUsedAt: tProp(types.maybeNull(types.string)).withTransform(
    isoStringToDateTransform(),
  ),

  meta: tProp(types.unchecked<Record<string, unknown>>(), () => ({})),
}) {
  @computed
  get isLastUsed() {
    if (!this.lastUsedAt) return false;
    const methods = this.root?.tilliX.paymentMethods.data;
    if (!methods) return false;

    const thisDate = this.lastUsedAt;
    return !methods.some(
      (m) => m.id !== this.id && m.lastUsedAt && m.lastUsedAt > thisDate,
    );
  }

  @computed
  get disabled() {
    return this.disabledReason !== null;
  }

  @computed
  get parsedCardExpirationDate(): { month: number; year: number } | null {
    if (this.type !== PaymentMethodType.CARD) {
      return null;
    }
    if (
      this.meta &&
      "cardExpirationMonth" in this.meta &&
      typeof this.meta.cardExpirationMonth === "string" &&
      "cardExpirationYear" in this.meta &&
      typeof this.meta.cardExpirationYear === "string"
    ) {
      const month = parseInt(this.meta.cardExpirationMonth, 10);
      const year = parseInt(this.meta.cardExpirationYear, 10);
      if (Number.isFinite(month) && Number.isFinite(year)) {
        return { month, year };
      }
    }
    return null;
  }

  @computed
  get canEditCardExpiration(): boolean {
    if (this.type !== PaymentMethodType.CARD) {
      return false;
    }
    const expirationDate = this.parsedCardExpirationDate;
    if (!expirationDate) {
      return false;
    }
    return isCardExpiryEndWithinNextMonthWindow(
      expirationDate.month,
      expirationDate.year,
    );
  }

  @computed
  get cardExpirationMonth(): number | null {
    return this.parsedCardExpirationDate?.month ?? null;
  }

  @computed
  get cardExpirationYear(): number | null {
    return this.parsedCardExpirationDate?.year ?? null;
  }

  @computed
  get bankAccountType(): "CHECKING" | "SAVINGS" | null {
    if (this.type !== PaymentMethodType.BANK_ACCOUNT) {
      return null;
    }
    if (
      this.meta &&
      "bankAccountType" in this.meta &&
      (this.meta.bankAccountType === "CHECKING" ||
        this.meta.bankAccountType === "SAVINGS")
    ) {
      return this.meta.bankAccountType;
    }
    return null;
  }

  @computed
  get isEnrolledInAutopay(): boolean {
    if (!this.root) {
      return false;
    }
    return this.root.tilliX.autoPayEnrollments.data.some(
      (e) => e.paymentMethodId === this.id,
    );
  }

  @modelAction
  protected _setValueForKey<K extends keyof this & keyof Self<this>>(
    key: K,
    value: Self<this>[K] | this[K],
  ): void {
    this[key] = value;
  }
}

@paymentNS.model
export class PaymentModel extends ExtendedModel(TModel, {
  confirmationNumber: tProp(types.or(types.string, types.null)),
  discount: tProp(types.number),
  fee: tProp(types.number),
  paidAt: tProp(types.string).withTransform(isoStringToDateTransform()),
  amount: tProp(types.number),
  status: tProp(types.string),
  description: tProp(types.string),
  serviceAccountId: tProp(types.string),
  paymentMethodId: tProp(types.maybeNull(types.string)),
  meta: tProp(types.unchecked<Record<string, unknown>>(), () => ({})),
}) {
  @computed
  get donation(): number {
    return typeof this.meta.donation === "number" ? this.meta.donation : 0;
  }

  @computed
  get paymentInfo():
    | { type: "card" | "check"; lastFour: string; label: string }
    | undefined {
    const info = this.meta.paymentInfo;
    if (
      info &&
      typeof info === "object" &&
      "type" in info &&
      "lastFour" in info &&
      "label" in info
    ) {
      return info as {
        type: "card" | "check";
        lastFour: string;
        label: string;
      };
    }
    // biome-ignore lint/nursery/noUselessUndefined: <required for getter>
    return undefined;
  }

  @computed
  get paymentMethod(): PaymentMethodModel | undefined {
    if (!this.detaching && this.root && this.paymentMethodId) {
      const method = this.root.tilliX.paymentMethods.modelRef(
        this.paymentMethodId,
      );

      return method.maybeCurrent;
    }

    // biome-ignore lint/nursery/noUselessUndefined: <required for getter>
    return undefined;
  }

  @computed
  get serviceAccount(): ServiceAccountModel | undefined {
    if (!this.detaching && this.root) {
      const account = this.root.tilliX.serviceAccounts.modelRef(
        this.serviceAccountId,
      );

      return account.maybeCurrent;
    }

    // biome-ignore lint/nursery/noUselessUndefined: <required for getter>
    return undefined;
  }

  @modelAction
  protected _setValueForKey<K extends keyof this & keyof Self<this>>(
    key: K,
    value: Self<this>[K] | this[K],
  ): void {
    this[key] = value;
  }
}

@autoPayEnrollmentNS.model
export class AutoPayEnrollmentModel extends ExtendedModel(TModel, {
  serviceAccountId: tProp(types.string),
  paymentMethodId: tProp(types.maybeNull(types.string)),
  // step 1 values
  frequency: tProp(types.maybeNull(types.enum(AutoPayFrequency))),
  customFrequency: tProp(types.maybeNull(types.enum(CustomAutoPayFrequency))),
  customFrequencyDay: tProp(types.maybeNull(types.string)),
  maxPaymentAmountPreference: tProp(
    types.maybeNull(types.enum(MaxPaymentAmountPreference)),
  ),
  maxPaymentAmount: tProp(types.maybeNull(types.number)),

  notificationPreferences: tProp(types.unchecked<Record<string, unknown>>()),
}) {
  @computed
  get serviceAccount() {
    if (!this.detaching && this.root) {
      const account = this.root.tilliX.serviceAccounts.modelRef(
        this.serviceAccountId,
      );

      return account.maybeCurrent;
    }

    // biome-ignore lint/nursery/noUselessUndefined: <required for getter>
    return undefined;
  }

  @computed
  get paymentMethod(): PaymentMethodModel | undefined {
    if (!this.detaching && this.root && this.paymentMethodId) {
      const method = this.root.tilliX.paymentMethods.modelRef(
        this.paymentMethodId,
      );

      return method.maybeCurrent;
    }

    // biome-ignore lint/nursery/noUselessUndefined: <required for getter>
    return undefined;
  }
}
